import type { ReactNode } from "react";

import type { FactBox, Read } from "@/components/ui/box.schema";
import type { SessionWorkspace } from "@/lib/api/sessionPortal";
import { attendanceLine, changeNote, whenWords } from "@/lib/session-learner";

import { RecordedClassJourney } from "../RecordedClassJourney";
import { InstructorNote, PublishedPlan, Readings } from "../SessionLearner";
import { SESSION_COPY, sessionName } from "./session-copy";
import { joinUrl } from "./use-teach-box";

const copy = SESSION_COPY.learner;

/** A read that has its value: the workspace as the page read it. */
function valueOf(read: Read<SessionWorkspace>): SessionWorkspace | null {
  return read.status === "ready" ? read.value : read.status === "stale" ? read.last : null;
}

function readingCount(value: SessionWorkspace): number {
  return (value.plan.unit?.materials ?? []).filter((item) => !("placement_id" in item)).length;
}

/**
 * S07 Get ready, or S08 Does not meet: a Fact. Before class its answer is the
 * session's objective, with what to do before it; Join leaves for the
 * provider when it meets (CASE-027). A plan not yet published is said as its
 * answer, and Join still stands. A cancelled session's answer is "Does not
 * meet", with no Join; work due still opens from Hand in.
 */
export function useGetReadyBox({ read, rank, where, onRetry }: {
  read: Read<SessionWorkspace>;
  rank: number | null;
  /** Where it meets, read only. */
  where: ReactNode;
  onRetry: () => void;
}): FactBox<SessionWorkspace> {
  const workspace = valueOf(read);
  const cancelled = workspace?.session.status === "cancelled";
  const url = workspace && !cancelled ? joinUrl(workspace) : null;
  const unit = workspace?.plan.unit ?? null;
  const readings = workspace && unit ? readingCount(workspace) : 0;
  return {
    kind: "fact",
    id: "get-ready",
    job: copy.getReadyJob,
    label: copy.getReady,
    owner: { system: "imam" },
    read,
    prose: (value) => {
      if (value.session.status === "cancelled") return copy.doesNotMeet;
      const posted = value.plan.unit;
      return posted ? posted.learning_objectives[0]?.trim() || posted.topic : copy.nothingPosted;
    },
    detail: (value) => {
      if (value.session.status === "cancelled") return copy.wasSet(whenWords(value.session.starts_at, value.course.timezone));
      const before = value.plan.unit?.preparation_guidance?.trim();
      return before ? copy.before(before) : whenWords(value.session.starts_at, value.course.timezone);
    },
    sub: cancelled ? undefined : !unit ? () => copy.postedLater : readings ? () => copy.readings(readings) : undefined,
    act: url ? { verb: SESSION_COPY.teach.join(workspace!.destination.meeting_provider), does: "leave", providerUrl: url } : undefined,
    candidate: url ? { eligible: true, blocked: null, rank } : undefined,
    body: (value) => {
      const note = changeNote(value);
      if (value.session.status === "cancelled") return (
        <div className="grid gap-4">
          {note && <InstructorNote note={note} />}
          {value.plan.unit && <PublishedPlan workspace={value} />}
        </div>
      );
      const pending = value.session.status !== "held" || value.session.clock_phase === "scheduled_now";
      return (
        <div className="grid gap-4">
          <div className="grid gap-1">{where}</div>
          {note && <InstructorNote note={note} />}
          {value.plan.unit ? <PublishedPlan workspace={value} /> : <p className="text-ui text-muted-foreground">{copy.postedLater}</p>}
          <Readings workspace={value} onRetry={onRetry} />
          {pending && <RecordedClassJourney part="notice" sessionId={value.session.id} classOrdinal={value.session.session_no} />}
        </div>
      );
    },
    // Join is the face's one act (F1): with it, Get ready stays open, its head keeps Join (CASE-047) and its body is drawn.
    exits: url ? undefined : [{ label: copy.show, reopens: true }],
    fold: url ? "never" : "choice",
  };
}

/**
 * S09 After class: a Fact. Once the session is held its answer is the recap,
 * with next steps and the learner's own attendance; "No recap yet" is a
 * sentence, never an empty heading. Before then it says when they appear.
 * A cancelled session has no After class.
 */
export function useAfterClassBox({ read }: { read: Read<SessionWorkspace> }): FactBox<SessionWorkspace> | null {
  const workspace = valueOf(read);
  if (workspace?.session.status === "cancelled") return null;
  return {
    kind: "fact",
    id: "after-class",
    job: copy.afterJob,
    label: copy.after,
    owner: { system: "imam" },
    read,
    prose: (value) => (value.session.status !== "held" ? copy.notHeld : value.plan.unit?.recap?.trim() || copy.noRecap),
    detail: (value) => {
      if (value.session.status !== "held") {
        return copy.appearsWhen(value.session.clock_phase === "status_pending" ? copy.instructorCloses : copy.isHeld(sessionName(value.session)));
      }
      const next = value.plan.unit?.follow_up?.trim();
      return next ? copy.nextSteps(next) : copy.noNextSteps;
    },
    sub: workspace?.session.status === "held"
      ? (value) => [copy.held(sessionName(value.session)), attendanceLine(value)].filter(Boolean).join(" · ")
      : undefined,
    body: (value) => <RecordedClassJourney part="recordings" sessionId={value.session.id} classOrdinal={value.session.session_no} />,
    exits: [{ label: copy.recordings, reopens: true }],
    fold: "choice",
  };
}
